import { useState } from 'react';
import { safeFormat } from '@/lib/safeDate';
import { useTranslation } from 'react-i18next';
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { usePayments } from '@/hooks/usePayments';
import { usePaymentTransactions } from '@/hooks/usePaymentTransactions';
import { usePaymentTransactionsByDeal, PaymentTransactionSummary, UnlinkedTransaction } from '@/hooks/usePaymentTransactionsByDeal';
import { LinkTransactionModal } from '@/components/deals/LinkTransactionModal';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Link } from 'react-router-dom';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DollarSign, Calendar, Plus, AlertCircle, CheckCircle, Clock, RefreshCcw, ChevronRight, Pencil, CreditCard, Link as LinkIcon, Loader2, History, Zap, ZapOff, Trash2 } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { format, addWeeks, addMonths } from 'date-fns';
import { parseDate } from '@/lib/localizedDateUtils';
import { isPaymentOverdue, getDaysOverdue as getOverdueDays } from '@/lib/timezoneUtils';
import { getEffectivePaymentStatus } from '@/lib/paymentStatus';
import { getAutoChargeDefaults, hasDefaultStripeCard } from '@/lib/autoCharge';
import { formatCurrency } from '@/utils';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { ChargePaymentModal, type ChargePaymentData } from '@/components/finance/collection/ChargePaymentModal';
import { ChargeAttemptsHistory } from '@/components/finance/collection/ChargeAttemptsHistory';
import RegisterPaymentInlineModal from '@/components/shared/RegisterPaymentInlineModal';

interface PaymentPlanConfig {
  depositAmount: number;
  depositSplit: {
    number_of_payments: number;
    frequency: string;
    start_date: string;
  };
  installmentSplit: {
    number_of_payments: number;
    frequency: string;
    start_date: string;
  };
  depositCustomDates?: string[];
  depositCustomAmounts?: number[];
  installmentCustomDates?: string[];
  installmentCustomAmounts?: number[];
}

interface PaymentsTabProps {
  dealId: string;
  dealTotal: number;
  upsellCredits?: number;
  isEditable?: boolean;
  paymentPlanConfig?: PaymentPlanConfig;
  contactId?: string;
}

const statusColors = {
  pending: 'bg-gray-100 text-gray-800 border-gray-200',
  partially_paid: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  paid: 'bg-green-100 text-green-800 border-green-200',
  overdue: 'bg-red-100 text-red-800 border-red-200',
  cancelled: 'bg-gray-100 text-gray-800 border-gray-200'
};

const statusIcons = {
  pending: Clock,
  partially_paid: AlertCircle,
  paid: CheckCircle,
  overdue: AlertCircle,
  cancelled: AlertCircle
};

const statusLabelsPtBr: Record<string, string> = {
  pending: 'Pendente',
  partially_paid: 'Parcial',
  paid: 'Pago',
  overdue: 'Atrasado',
  cancelled: 'Cancelado',
};

// Localiza títulos como "Payment 12" -> "Pagamento 12"
function localizePaymentNumber(value?: string | null): string {
  if (!value) return '';
  const m = String(value).match(/^Payment\s+(\d+)$/i);
  if (m) return `Pagamento ${m[1]}`;
  return value;
}

// Localiza descrições como "Installment payment 11" -> "Parcela 11", "Deposit payment 1" -> "Entrada 1"
function localizePaymentDescription(value?: string | null): string {
  if (!value) return '';
  const s = String(value);
  let m = s.match(/^Installment\s+payment\s+(\d+)$/i);
  if (m) return `Parcela ${m[1]}`;
  m = s.match(/^Deposit\s+payment\s+(\d+)$/i);
  if (m) return `Entrada ${m[1]}`;
  return s;
}

export function PaymentsTab({
  dealId,
  dealTotal,
  upsellCredits = 0,
  isEditable = true,
  paymentPlanConfig,
  contactId
}: PaymentsTabProps) {
  const { t } = useTranslation();
  // Finance operations are centralized in /finance; Deal tab is read-only mirror.
  const allowDealPaymentMutations = true;
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const {
    payments,
    loading,
    createPayment,
    loadPayments,
    deletePayment,
    deleteAllPayments
  } = usePayments(dealId);
  const { data: transactionsData, refetch: refetchTransactions } = usePaymentTransactionsByDeal(dealId);
  const transactionsByPayment = transactionsData?.grouped || {};
  const unlinkedTransactions = transactionsData?.unlinked || [];
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [isRecordDialogOpen, setIsRecordDialogOpen] = useState(false);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [selectedPayments, setSelectedPayments] = useState<string[]>([]);
  const [paymentFormData, setPaymentFormData] = useState({
    description: '',
    amount: '',
    due_date: '',
    payment_type: 'installment'
  });
  const [transactionFormData, setTransactionFormData] = useState({
    transaction_date: safeFormat(new Date(), 'yyyy-MM-dd'),
    amount: '',
    payment_method: 'cash',
    reference_number: '',
    notes: ''
  });
  const [partialPaymentId, setPartialPaymentId] = useState<string | null>(null);
  const [partialAmounts, setPartialAmounts] = useState<Record<string, string>>({});
  
  const [editingTransaction, setEditingTransaction] = useState<PaymentTransactionSummary | null>(null);
  const [isEditTransactionDialogOpen, setIsEditTransactionDialogOpen] = useState(false);
  const [editTransactionFormData, setEditTransactionFormData] = useState({
    transaction_date: '',
    amount: '',
    payment_method: '',
    reference_number: '',
    notes: ''
  });
  const [chargeModalPayment, setChargeModalPayment] = useState<ChargePaymentData | null>(null);
  const [registerPaymentModalPayment, setRegisterPaymentModalPayment] = useState<{
    id: string;
    amount: number;
    paid_amount: number;
    balance: number;
    currency?: string | null;
    description?: string | null;
    business_unit?: string | null;
  } | null>(null);
  const [linkConfirmPaymentId, setLinkConfirmPaymentId] = useState<string | null>(null);
  const [historyPaymentId, setHistoryPaymentId] = useState<string | null>(null);
  const [historyPaymentDesc, setHistoryPaymentDesc] = useState<string>('');
  const [deletePaymentId, setDeletePaymentId] = useState<string | null>(null);
  const [isDeletingPayment, setIsDeletingPayment] = useState(false);
  const [isRecordingTransaction, setIsRecordingTransaction] = useState(false);
  const [linkTransactionModalOpen, setLinkTransactionModalOpen] = useState(false);
  const [selectedUnlinkedTransaction, setSelectedUnlinkedTransaction] = useState<UnlinkedTransaction | null>(null);

  // Fetch contact info to check if has saved card AND get name
  const { data: contactInfo } = useQuery({
    queryKey: ['contact-stripe-info', contactId],
    queryFn: async () => {
      if (!contactId) return null;
      const { data, error } = await supabase
        .from('contacts')
        .select('id, first_name, last_name, stripe_customer_id, stripe_default_payment_method_id, has_payment_method')
        .eq('id', contactId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!contactId
  });

  // Fetch deal info for name
  const { data: dealInfo } = useQuery({
    queryKey: ['deal-info-for-payments', dealId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('deals')
        .select('id, name')
        .eq('id', dealId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!dealId
  });

  const hasCard = hasDefaultStripeCard(contactInfo);
  const contactName = contactInfo 
    ? `${contactInfo.first_name || ''} ${contactInfo.last_name || ''}`.trim() || 'Cliente'
    : 'Cliente';
  const dealName = dealInfo?.name || 'Deal';

  // Count auto-charge enabled payments
  const autoChargeCount = payments.filter(p => p && p.auto_charge_enabled && p.status !== 'paid' && p.status !== 'cancelled').length;
  const pendingPaymentsCount = payments.filter(p => p && p.status !== 'paid' && p.status !== 'cancelled').length;

  // Mutation for toggling auto-charge
  const toggleAutoChargeMutation = useMutation({
    mutationFn: async ({ paymentId, enabled }: { paymentId: string; enabled: boolean }) => {
      const { error } = await supabase
        .from('payments')
        .update({ 
          auto_charge_enabled: enabled,
          auto_charge_status: enabled ? 'pending' : null,
          auto_charge_attempts: enabled ? 0 : 0
        })
        .eq('id', paymentId);
      
      if (error) throw error;
    },
    onSuccess: (_, { enabled }) => {
      loadPayments();
      toast.success(enabled ? 'Auto-charge ativado' : 'Auto-charge desativado');
    },
    onError: (error: any) => {
      toast.error(`Erro: ${error.message}`);
    }
  });

  // Bulk toggle auto-charge
  const bulkToggleAutoCharge = async (enable: boolean) => {
    const unpaidPaymentIds = payments
      .filter(p => p && p.status !== 'paid' && p.status !== 'cancelled')
      .map(p => p.id);
    
    if (unpaidPaymentIds.length === 0) {
      toast.warning('Não há parcelas pendentes');
      return;
    }

    try {
      const { error } = await supabase
        .from('payments')
        .update({ 
          auto_charge_enabled: enable,
          auto_charge_status: enable ? 'pending' : null,
          auto_charge_attempts: 0
        })
        .in('id', unpaidPaymentIds);
      
      if (error) throw error;
      
      await loadPayments();
      toast.success(enable 
        ? `Auto-charge ativado para ${unpaidPaymentIds.length} parcelas`
        : `Auto-charge desativado para ${unpaidPaymentIds.length} parcelas`
      );
    } catch (error: any) {
      toast.error(`Erro: ${error.message}`);
    }
  };

  // Mutation for charging a payment - returns result for modal
  const chargeMutation = useMutation({
    mutationFn: async (paymentId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('stripe-charge-saved-card', {
        body: { 
          payment_id: paymentId, 
          force: true,
          triggered_by: user.id,
          trigger_type: 'manual'
        }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // NÃO fazer loadPayments aqui - isso causa re-render que reseta o modal
      // O refresh será feito quando o modal fechar via onOpenChange
      queryClient.invalidateQueries({ queryKey: ['charge-history'] });
      queryClient.invalidateQueries({ queryKey: ['payment-transactions'] });
    }
  });

  // Helper to handle charge with modal feedback
  const handleChargeWithModal = async (paymentId: string): Promise<{ success: boolean; error?: string; amount_charged?: number }> => {
    try {
      const data = await chargeMutation.mutateAsync(paymentId);
      if (data?.success) {
        return { success: true, amount_charged: data.amount_charged };
      } else {
        return { success: false, error: data?.message || data?.error || 'Erro na cobrança' };
      }
    } catch (error: any) {
      return { success: false, error: error.message || 'Erro desconhecido' };
    }
  };

  // Mutation for generating payment link
  const linkMutation = useMutation({
    mutationFn: async ({ paymentId, amount }: { paymentId: string; amount: number }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('stripe-create-payment-link', {
        body: {
          contact_id: contactId,
          payment_id: paymentId,
          amount: amount,
          origin_context: 'deal_payments_tab',
          mode: 'payment',
          success_url: window.location.href,
          cancel_url: window.location.href
        }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      if (data?.url) {
        window.open(data.url, '_blank');
        toast.success('Link de pagamento gerado');
      }
      setLinkConfirmPaymentId(null);
    },
    onError: (error: any) => {
      toast.error(`Erro ao gerar link: ${error.message}`);
      setLinkConfirmPaymentId(null);
    }
  });

  const handleCreatePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!allowDealPaymentMutations) {
      toast.info('Lançamentos foram centralizados no módulo Financeiro.');
      return;
    }
    try {
      await createPayment({
        deal_id: dealId,
        description: paymentFormData.description,
        amount: parseFloat(paymentFormData.amount),
        due_date: paymentFormData.due_date,
        payment_type: paymentFormData.payment_type,
        status: 'pending',
        ...getAutoChargeDefaults(hasCard)
      });
      setIsPaymentDialogOpen(false);
      setPaymentFormData({
        description: '',
        amount: '',
        due_date: '',
        payment_type: 'installment'
      });
    } catch (error) {
      console.error('Error creating payment:', error);
    }
  };

  const handleRecordTransaction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!allowDealPaymentMutations) {
      toast.info('Registre pagamentos no módulo Financeiro > Recebíveis.');
      return;
    }
    if (isRecordingTransaction) return; // Prevent double-click
    if (selectedPayments.length === 0) {
      toast.error(t('paymentsTab.selectAtLeastOne'));
      return;
    }
    setIsRecordingTransaction(true);
    try {
      const totalAmount = parseFloat(transactionFormData.amount);
      if (isNaN(totalAmount) || totalAmount <= 0) {
        toast.error(t('paymentsTab.enterValidAmount'));
        return;
      }

      // Buscar usuário diretamente do Supabase para garantir que temos o ID
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser?.id) {
        toast.error('Usuário não autenticado. Faça login novamente.');
        return;
      }

      // Buscar o app_users correspondente ao auth.user
      const { data: appUser, error: appUserError } = await supabase
        .from('app_users')
        .select('id')
        .eq('auth_user_id', currentUser.id)
        .single();

      if (appUserError || !appUser?.id) {
        console.error('Error fetching app user:', appUserError);
        toast.error('Usuário não encontrado no sistema. Contate o administrador.');
        return;
      }

      // Get selected payments sorted by due date
      const paymentsToProcess = payments.filter(p => selectedPayments.includes(p.id)).sort((a, b) => (parseDate(a.due_date)?.getTime() || 0) - (parseDate(b.due_date)?.getTime() || 0));
      let remainingAmount = totalAmount;

      // Distribute amount across selected payments
      for (const payment of paymentsToProcess) {
        if (remainingAmount <= 0) break;
        const paymentBalance = payment.balance || 0;
        const amountToApply = Math.min(remainingAmount, paymentBalance);
        if (amountToApply > 0) {
          // Create transaction for this payment
          const {
            error
          } = await supabase.from('payment_transactions').insert({
            payment_id: payment.id,
            deal_id: dealId,
            transaction_date: transactionFormData.transaction_date,
            amount: amountToApply,
            payment_method: transactionFormData.payment_method,
            reference_number: transactionFormData.reference_number,
            notes: transactionFormData.notes,
            status: 'confirmed',
            recorded_by: appUser.id
          });
          if (error) throw error;
          remainingAmount -= amountToApply;
        }
      }
      await loadPayments();
      await refetchTransactions();
      // Invalidar cache do relatório de auditoria
      queryClient.invalidateQueries({ queryKey: ['payment-audit-report'] });
      setIsRecordDialogOpen(false);
      setSelectedPayments([]);
      setPartialPaymentId(null);
      setPartialAmounts({});
      setTransactionFormData({
        transaction_date: safeFormat(new Date(), 'yyyy-MM-dd'),
        amount: '',
        payment_method: 'cash',
        reference_number: '',
        notes: ''
      });
      if (remainingAmount > 0) {
        toast.warning(t('paymentsTab.amountRemainingAfterApply', { amount: formatCurrency(remainingAmount) }));
      } else {
        toast.success(t('paymentsTab.paymentRecordedSuccess'));
      }
    } catch (error) {
      console.error('Error recording transaction:', error);
      toast.error(t('paymentsTab.errorRecordingPayment'));
    } finally {
      setIsRecordingTransaction(false);
    }
  };

  // Handle opening edit transaction dialog
  const handleEditTransaction = (transaction: PaymentTransactionSummary) => {
    setEditingTransaction(transaction);
    setEditTransactionFormData({
      transaction_date: transaction.transaction_date.split('T')[0],
      amount: transaction.amount.toString(),
      payment_method: transaction.payment_method,
      reference_number: transaction.reference_number || '',
      notes: transaction.notes || ''
    });
    setIsEditTransactionDialogOpen(true);
  };

  // Handle saving edited transaction
  const handleSaveEditedTransaction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!allowDealPaymentMutations) {
      toast.info('Edição de transações está centralizada no módulo Financeiro.');
      return;
    }
    if (!editingTransaction) return;

    try {
      const { error } = await supabase
        .from('payment_transactions')
        .update({
          transaction_date: editTransactionFormData.transaction_date,
          amount: parseFloat(editTransactionFormData.amount),
          payment_method: editTransactionFormData.payment_method,
          reference_number: editTransactionFormData.reference_number || null,
          notes: editTransactionFormData.notes || null
        })
        .eq('id', editingTransaction.id);

      if (error) throw error;

      await loadPayments();
      await refetchTransactions();
      setIsEditTransactionDialogOpen(false);
      setEditingTransaction(null);
      toast.success(t('paymentsTab.transactionUpdated', 'Transação atualizada com sucesso'));
    } catch (error) {
      console.error('Error updating transaction:', error);
      toast.error(t('paymentsTab.errorUpdatingTransaction', 'Erro ao atualizar transação'));
    }
  };


  // Helper function to round to nearest 5
  const roundToNearestFive = (num: number) => {
    return Math.round(num / 5) * 5;
  };

  // Generate payment schedule from config
  const generatePaymentScheduleFromConfig = () => {
    if (!paymentPlanConfig) return [];
    
    const { depositAmount, depositSplit, installmentSplit, depositCustomDates, depositCustomAmounts, installmentCustomDates, installmentCustomAmounts } = paymentPlanConfig;
    const effectiveTotal = dealTotal - (upsellCredits || 0);
    const installmentAmount = effectiveTotal - depositAmount;
    
    // Generate deposit schedule
    const depositSchedule: Array<{ number: number; amount: number; due_date: Date; type: string }> = [];
    const depositPerPayment = depositAmount / (depositSplit.number_of_payments || 1);
    
    if (depositSplit.frequency === "custom") {
      for (let i = 0; i < depositSplit.number_of_payments; i++) {
        const customDate = depositCustomDates?.[i];
        const customAmount = depositCustomAmounts?.[i] || depositPerPayment;
        if (customDate) {
          const [year, month, day] = customDate.split('-').map(Number);
          depositSchedule.push({
            number: i + 1,
            amount: customAmount,
            due_date: new Date(year, month - 1, day),
            type: "deposit"
          });
        }
      }
    } else if (depositSplit.start_date) {
      const [year, month, day] = depositSplit.start_date.split('-').map(Number);
      const startDate = new Date(year, month - 1, day);
      
      for (let i = 0; i < depositSplit.number_of_payments; i++) {
        let dueDate;
        if (i === 0) {
          dueDate = startDate;
        } else {
          switch (depositSplit.frequency) {
            case "weekly":
              dueDate = addWeeks(startDate, i);
              break;
            case "biweekly":
              dueDate = addWeeks(startDate, i * 2);
              break;
            case "monthly":
              dueDate = addMonths(startDate, i);
              break;
            default:
              dueDate = addWeeks(startDate, i);
          }
        }
        
        depositSchedule.push({
          number: i + 1,
          amount: depositPerPayment,
          due_date: dueDate,
          type: "deposit"
        });
      }
    }
    
    // Generate installment schedule
    const installmentSchedule: Array<{ number: number; amount: number; due_date: Date; type: string }> = [];
    const numInstallments = installmentSplit.number_of_payments || 1;
    
    // Calculate installment amounts with rounding
    let regularAmount = installmentAmount / numInstallments;
    let lastPayment = installmentAmount;
    
    if (numInstallments > 1) {
      regularAmount = roundToNearestFive(installmentAmount / numInstallments);
      const totalOfRegularPayments = regularAmount * (numInstallments - 1);
      lastPayment = installmentAmount - totalOfRegularPayments;
    }
    
    if (installmentSplit.frequency === "custom") {
      for (let i = 0; i < numInstallments; i++) {
        const customDate = installmentCustomDates?.[i];
        const customAmount = installmentCustomAmounts?.[i] || regularAmount;
        if (customDate) {
          const [year, month, day] = customDate.split('-').map(Number);
          installmentSchedule.push({
            number: i + 1,
            amount: customAmount,
            due_date: new Date(year, month - 1, day),
            type: "installment"
          });
        }
      }
    } else if (installmentSplit.start_date) {
      const [year, month, day] = installmentSplit.start_date.split('-').map(Number);
      const startDate = new Date(year, month - 1, day);
      
      for (let i = 0; i < numInstallments; i++) {
        let dueDate;
        if (i === 0) {
          dueDate = startDate;
        } else {
          switch (installmentSplit.frequency) {
            case "weekly":
              dueDate = addWeeks(startDate, i);
              break;
            case "biweekly":
              dueDate = addWeeks(startDate, i * 2);
              break;
            case "monthly":
              dueDate = addMonths(startDate, i);
              break;
            default:
              dueDate = addMonths(startDate, i);
          }
        }
        
        const amount = (i === numInstallments - 1) ? lastPayment : regularAmount;
        
        installmentSchedule.push({
          number: i + 1,
          amount: amount,
          due_date: dueDate,
          type: "installment"
        });
      }
    }
    
    // Combine, filter out zero/negative amounts, and sort by date
    return [...depositSchedule, ...installmentSchedule]
      .filter(payment => payment.amount > 0)
      .sort((a, b) => a.due_date.getTime() - b.due_date.getTime());
  };

  const handleResetPayments = async () => {
    if (!allowDealPaymentMutations) {
      toast.info('Reset de parcelas está centralizado no módulo Financeiro.');
      return;
    }
    if (!paymentPlanConfig) {
      toast.error(t('paymentsTab.noConfigFound') || 'No payment plan configuration found. Please configure the payment plan in the Overview tab first.');
      return;
    }
    
    setIsResetting(true);
    try {
      // 1. Delete all existing payments
      await deleteAllPayments();
      
      // 2. Generate new schedule from config
      const schedule = generatePaymentScheduleFromConfig();
      
      if (schedule.length === 0) {
        toast.error(t('paymentsTab.noPaymentsGenerated') || 'Could not generate payments from configuration.');
        return;
      }
      
      // 3. Create new payments (filter out zero amounts to avoid constraint violation)
      const paymentsToCreate = schedule
        .filter(payment => payment.amount > 0)
        .map((payment, index) => ({
          deal_id: dealId,
          payment_number: `Payment ${index + 1}`,
          installment_number: index + 1,
          payment_type: payment.type === 'deposit' ? 'down_payment' : payment.type,
          description: `${payment.type === 'deposit' ? 'Deposit' : 'Installment'} payment ${payment.number}`,
          amount: payment.amount,
          due_date: safeFormat(payment.due_date, 'yyyy-MM-dd'),
          status: 'pending',
          paid_amount: 0,
          balance: payment.amount,
          ...getAutoChargeDefaults(hasCard),
        }));

      const { error: insertError } = await supabase
        .from('payments')
        .insert(paymentsToCreate);
      
      if (insertError) throw insertError;
      
      await loadPayments();
      setIsResetDialogOpen(false);
      toast.success(t('paymentsTab.paymentsRegenerated') || 'Payment schedule regenerated successfully!');
    } catch (error) {
      console.error('Error resetting payments:', error);
      toast.error(t('paymentsTab.errorResettingPayments') || 'Error resetting payments');
    } finally {
      setIsResetting(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8">{t('paymentsTab.loadingPayments')}</div>;
  }

  const totalPaid = payments.reduce((sum, p) => sum + (p.paid_amount || 0), 0);
  const totalBalance = payments.reduce((sum, p) => sum + (p.balance || 0), 0);
  const calculatedTotal = payments.reduce((sum, p) => sum + (p.amount || 0), 0);

  // Calculate overdue balance (using Orlando timezone for business logic)
  const totalOverdue = payments.filter(p => {
    return isPaymentOverdue(p.due_date) && p.status !== 'paid' && p.status !== 'cancelled';
  }).reduce((sum, p) => sum + (p.balance || 0), 0);
  const progressPercentage = calculatedTotal > 0 ? totalPaid / calculatedTotal * 100 : 0;

  // Get unpaid/partially paid payments sorted by due date
  const unpaidPayments = payments.filter(p => p.status !== 'paid' && p.status !== 'cancelled' && (p.balance || 0) > 0).sort((a, b) => (parseDate(a.due_date)?.getTime() || 0) - (parseDate(b.due_date)?.getTime() || 0));

  // Calculate total balance of selected payments
  const selectedTotalBalance = payments.filter(p => selectedPayments.includes(p.id)).reduce((sum, p) => sum + (p.balance || 0), 0);

  // Update amount field when selections change
  const handlePaymentSelection = (paymentId: string, checked: boolean) => {
    let newSelection: string[];
    if (checked) {
      // Find the payment being added
      const paymentToAdd = unpaidPayments.find(p => p.id === paymentId);
      if (!paymentToAdd) return;

      // Get all payments up to and including this one (enforce chronological order)
      const paymentIndex = unpaidPayments.findIndex(p => p.id === paymentId);
      newSelection = unpaidPayments.slice(0, paymentIndex + 1).map(p => p.id);
    } else {
      // Remove this payment and all payments after it
      const paymentIndex = unpaidPayments.findIndex(p => p.id === paymentId);
      newSelection = unpaidPayments.slice(0, paymentIndex).map(p => p.id);

      // Clear partial if unchecking a payment
      if (partialPaymentId === paymentId) {
        setPartialPaymentId(null);
      }
    }
    setSelectedPayments(newSelection);

    // Calculate total balance for selected payments
    const totalBalance = unpaidPayments.filter(p => newSelection.includes(p.id)).reduce((sum, p) => sum + (p.balance || 0), 0);

    // Auto-fill amount field
    setTransactionFormData(prev => ({
      ...prev,
      amount: totalBalance.toFixed(2)
    }));
  };

  const handlePartialToggle = (paymentId: string, isPartial: boolean) => {
    if (isPartial) {
      setPartialPaymentId(paymentId);
      // When marking as partial, remove all payments after this one
      const paymentIndex = unpaidPayments.findIndex(p => p.id === paymentId);
      const newSelection = unpaidPayments.slice(0, paymentIndex + 1).map(p => p.id);
      setSelectedPayments(newSelection);

      // Initialize partial amount with full balance
      const payment = unpaidPayments.find(p => p.id === paymentId);
      if (payment) {
        setPartialAmounts({
          ...partialAmounts,
          [paymentId]: payment.balance.toString()
        });
      }

      // Calculate total
      recalculateAmount(newSelection, {
        ...partialAmounts,
        [paymentId]: payment?.balance.toString() || '0'
      });
    } else {
      setPartialPaymentId(null);
      const newPartialAmounts = {
        ...partialAmounts
      };
      delete newPartialAmounts[paymentId];
      setPartialAmounts(newPartialAmounts);

      // Recalculate full amount
      const totalBalance = unpaidPayments.filter(p => selectedPayments.includes(p.id)).reduce((sum, p) => sum + (p.balance || 0), 0);
      setTransactionFormData(prev => ({
        ...prev,
        amount: totalBalance.toFixed(2)
      }));
    }
  };

  const handlePartialAmountChange = (paymentId: string, value: string) => {
    const newPartialAmounts = {
      ...partialAmounts,
      [paymentId]: value
    };
    setPartialAmounts(newPartialAmounts);
    recalculateAmount(selectedPayments, newPartialAmounts);
  };

  const recalculateAmount = (selection: string[], partialAmts: Record<string, string>) => {
    let total = 0;
    selection.forEach(id => {
      const payment = unpaidPayments.find(p => p.id === id);
      if (payment) {
        if (partialAmts[id]) {
          total += parseFloat(partialAmts[id]) || 0;
        } else {
          total += payment.balance || 0;
        }
      }
    });
    setTransactionFormData(prev => ({
      ...prev,
      amount: total.toFixed(2)
    }));
  };

  // Helper to calculate days overdue (using Orlando timezone for business logic)
  const getDaysOverdue = (dueDate: string) => getOverdueDays(dueDate);

  return (
    <div className="space-y-6">
      {!allowDealPaymentMutations ? (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="p-3 text-sm text-blue-900 space-y-3">
            <p>
              Esta aba está em modo espelho. Lançamentos e ajustes financeiros foram centralizados no módulo <strong>Financeiro</strong>.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button asChild size="sm" variant="outline" className="bg-white">
                <Link to="/finance/receivables">Dar Baixa em Recebíveis</Link>
              </Button>
              <Button asChild size="sm" variant="outline" className="bg-white">
                <Link to="/finance/collection?tab=automatico">Processar Cobrança (Automático)</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">{t('paymentsTab.totalDeal')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(calculatedTotal)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">{t('paymentsTab.paid')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{formatCurrency(totalPaid)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">{t('paymentsTab.balance')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{formatCurrency(totalBalance)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">{t('paymentsTab.overdue')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{formatCurrency(totalOverdue)}</div>
          </CardContent>
        </Card>
        {/* Auto-Charge Summary Card */}
        <Card className={hasCard ? 'border-blue-200 bg-blue-50/30' : 'border-gray-200'}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <Zap className="h-3.5 w-3.5" />
              Auto-Charge
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <span className={`text-2xl font-bold ${hasCard ? 'text-blue-600' : 'text-gray-400'}`}>
                {autoChargeCount}/{pendingPaymentsCount}
              </span>
              {hasCard ? (
                <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                  <CreditCard className="h-3 w-3 mr-1" />
                  Cartão OK
                </Badge>
              ) : (
                <Badge variant="outline" className="text-xs bg-gray-50 text-gray-500 border-gray-200">
                  Sem Cartão
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Unlinked Transactions (finance-only operation, hidden in mirror mode) */}
      {allowDealPaymentMutations && unlinkedTransactions.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-amber-600" />
              Transações Aguardando Vinculação ({unlinkedTransactions.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {unlinkedTransactions.map(tx => (
              <div key={tx.id} className="flex items-center justify-between p-3 bg-background rounded-lg border">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">
                      Não Vinculada
                    </Badge>
                    <span className="font-medium">{formatCurrency(tx.amount)}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {safeFormat(parseDate(tx.transaction_date)!, 'dd/MM/yyyy')} • {tx.payment_method}
                    {tx.notes && <span className="ml-2">• {tx.notes}</span>}
                  </div>
                </div>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => {
                    setSelectedUnlinkedTransaction(tx);
                    setLinkTransactionModalOpen(true);
                  }}
                  className="text-blue-600 border-blue-200 hover:bg-blue-50"
                >
                  <LinkIcon className="h-3 w-3 mr-1" />
                  Vincular
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Bulk Auto-Charge Actions */}
      {allowDealPaymentMutations && hasCard && pendingPaymentsCount > 0 && (
        <div className="flex items-center gap-2 p-3 bg-blue-50/50 rounded-lg border border-blue-100">
          <Zap className="h-4 w-4 text-blue-600" />
          <span className="text-sm text-blue-800 font-medium">Auto-Charge em Lote:</span>
          <Button
            variant="outline"
            size="sm"
            className="text-green-600 border-green-200 hover:bg-green-50"
            onClick={() => bulkToggleAutoCharge(true)}
            disabled={autoChargeCount === pendingPaymentsCount}
          >
            <Zap className="h-3.5 w-3.5 mr-1" />
            Ativar Todas
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-gray-600 border-gray-200 hover:bg-gray-50"
            onClick={() => bulkToggleAutoCharge(false)}
            disabled={autoChargeCount === 0}
          >
            <ZapOff className="h-3.5 w-3.5 mr-1" />
            Desativar Todas
          </Button>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">{t('paymentsTab.paymentSchedule')}</h3>
        <div className="flex gap-2">
          {allowDealPaymentMutations && (
            <>
              <Dialog open={isRecordDialogOpen} onOpenChange={setIsRecordDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline">
                    <DollarSign className="w-4 h-4 mr-2" />
                    {t('paymentsTab.recordPayment')}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{t('paymentsTab.recordPaymentTransaction')}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleRecordTransaction} className="space-y-4">
                    {/* Payment Selection */}
                    <div>
                      <Label className="text-base font-semibold mb-3 block">{t('paymentsTab.selectPayments')}</Label>
                      <div className="space-y-2 max-h-[200px] overflow-y-auto border rounded-md p-3">
                         {unpaidPayments.length === 0 ? (
                           <p className="text-sm text-muted-foreground text-center py-4">
                             {t('paymentsTab.noPendingPayments')}
                           </p>
                         ) : unpaidPayments.map((payment, index) => {
                        const isSelected = selectedPayments.includes(payment.id);
                        const lastSelectedIndex = selectedPayments.length ? Math.max(...selectedPayments.map(id => unpaidPayments.findIndex(p => p.id === id))) : -1;
                        const isDisabled = !isSelected && (index > lastSelectedIndex + 1 || partialPaymentId !== null);
                        const isPartial = partialPaymentId === payment.id;
                        const canBePartial = isSelected && index === lastSelectedIndex;
                        return (
                          <div key={payment.id} className={`flex items-start space-x-3 p-2 rounded ${isDisabled ? 'opacity-50' : 'hover:bg-slate-50'}`}>
                            <Checkbox checked={isSelected} disabled={isDisabled} onCheckedChange={checked => handlePaymentSelection(payment.id, !!checked)} />
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm">{payment.payment_number}</span>
                                  {canBePartial && (
                                    <div className="flex items-center gap-1">
                                      <Checkbox id={`partial-${payment.id}`} checked={isPartial} onCheckedChange={checked => handlePartialToggle(payment.id, !!checked)} className="h-3 w-3" />
                                      <Label htmlFor={`partial-${payment.id}`} className="text-xs text-muted-foreground cursor-pointer">
                                        {t('paymentsTab.partial')}
                                      </Label>
                                    </div>
                                  )}
                                </div>
                                {isPartial ? (
                                  <Input type="number" step="0.01" value={partialAmounts[payment.id] || payment.balance} onChange={e => handlePartialAmountChange(payment.id, e.target.value)} className="w-32 h-8 text-right text-sm font-semibold text-orange-600" max={payment.balance} />
                                ) : (
                                  <span className="text-sm font-semibold text-orange-600">
                                    {formatCurrency(payment.balance)}
                                  </span>
                                )}
                              </div>
                              {payment.description && <p className="text-xs text-muted-foreground">{localizePaymentDescription(payment.description)}</p>}
                              <p className="text-xs text-muted-foreground">
                                {t('paymentsTab.dueDate')}: {parseDate(payment.due_date) ? safeFormat(parseDate(payment.due_date)!, 'PP') : '-'}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                      </div>
                      {selectedPayments.length > 0 && (
                        <div className="mt-2 p-2 bg-blue-50 rounded text-sm">
                          <span className="font-medium">{selectedPayments.length} {t('paymentsTab.paymentsSelected')}</span>
                          <span className="mx-2">•</span>
                          <span>{t('paymentsTab.totalBalance')}: {formatCurrency(selectedTotalBalance)}</span>
                          {partialPaymentId && (
                            <>
                              <span className="mx-2">•</span>
                              <span className="text-orange-600 font-medium">{t('paymentsTab.partialPaymentEnabled')}</span>
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Transaction Details */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="transaction_date">{t('paymentsTab.paymentDate')} *</Label>
                        <Input id="transaction_date" type="date" value={transactionFormData.transaction_date} onChange={e => setTransactionFormData({
                        ...transactionFormData,
                        transaction_date: e.target.value
                      })} required />
                      </div>
                      <div>
                        <Label htmlFor="trans_amount">{t('paymentsTab.amountReceived')} *</Label>
                        <Input id="trans_amount" type="number" step="0.01" value={transactionFormData.amount} readOnly className="bg-muted cursor-not-allowed" />
                        <p className="text-xs text-muted-foreground mt-1">
                          {t('paymentsTab.autoCalculatedNote')}
                        </p>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="payment_method">{t('paymentsTab.paymentMethod')} *</Label>
                      <Select value={transactionFormData.payment_method} onValueChange={value => setTransactionFormData({
                      ...transactionFormData,
                      payment_method: value
                    })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="stripe">{t('paymentsTab.stripe')}</SelectItem>
                          <SelectItem value="zelle">{t('paymentsTab.zelle')}</SelectItem>
                          <SelectItem value="bank_transfer">{t('paymentsTab.bankTransfer')}</SelectItem>
                          <SelectItem value="cash">{t('paymentsTab.cash')}</SelectItem>
                          <SelectItem value="check">{t('paymentsTab.check')}</SelectItem>
                          <SelectItem value="pix">{t('paymentsTab.pix')}</SelectItem>
                          <SelectItem value="other">{t('paymentsTab.other')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="reference_number">{t('paymentsTab.referenceNumber')}</Label>
                      <Input id="reference_number" value={transactionFormData.reference_number} onChange={e => setTransactionFormData({
                      ...transactionFormData,
                      reference_number: e.target.value
                    })} placeholder={t('paymentsTab.referenceNumberPlaceholder')} />
                    </div>

                    <div>
                      <Label htmlFor="trans_notes">{t('paymentsTab.notes')}</Label>
                      <Textarea id="trans_notes" value={transactionFormData.notes} onChange={e => setTransactionFormData({
                      ...transactionFormData,
                      notes: e.target.value
                    })} rows={2} placeholder={t('paymentsTab.notesPlaceholder')} />
                    </div>

                    <div className="flex justify-end gap-2 pt-4 border-t">
                      <Button type="button" variant="outline" onClick={() => {
                      setIsRecordDialogOpen(false);
                      setSelectedPayments([]);
                      setPartialPaymentId(null);
                      setPartialAmounts({});
                    }}>
                        {t('common.cancel')}
                      </Button>
                      <Button type="submit" disabled={selectedPayments.length === 0 || isRecordingTransaction}>
                        {isRecordingTransaction ? 'Processando...' : t('paymentsTab.recordPayment')}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              <Dialog open={isPaymentDialogOpen} onOpenChange={setIsPaymentDialogOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="w-4 h-4 mr-2" />
                    {t('paymentsTab.addPayment')}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{t('paymentsTab.addPayment')}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreatePayment} className="space-y-4">
                    <div>
                      <Label htmlFor="description">{t('paymentsTab.description')}</Label>
                      <Input id="description" value={paymentFormData.description} onChange={e => setPaymentFormData({
                      ...paymentFormData,
                      description: e.target.value
                    })} placeholder={t('paymentsTab.descriptionPlaceholder')} />
                    </div>

                    <div>
                      <Label htmlFor="payment_type">{t('paymentsTab.paymentType')}</Label>
                      <Select value={paymentFormData.payment_type} onValueChange={value => setPaymentFormData({
                      ...paymentFormData,
                      payment_type: value
                    })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="deposit">{t('paymentsTab.deposit')}</SelectItem>
                          <SelectItem value="installment">{t('paymentsTab.installment')}</SelectItem>
                          <SelectItem value="final">{t('paymentsTab.finalPayment')}</SelectItem>
                          <SelectItem value="other">{t('paymentsTab.other')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="amount">{t('paymentsTab.amount')} *</Label>
                        <Input id="amount" type="number" step="0.01" value={paymentFormData.amount} onChange={e => setPaymentFormData({
                        ...paymentFormData,
                        amount: e.target.value
                      })} required />
                      </div>
                      <div>
                        <Label htmlFor="due_date">{t('paymentsTab.dueDate')} *</Label>
                        <Input id="due_date" type="date" value={paymentFormData.due_date} onChange={e => setPaymentFormData({
                        ...paymentFormData,
                        due_date: e.target.value
                      })} required />
                      </div>
                    </div>

                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" onClick={() => setIsPaymentDialogOpen(false)}>
                        {t('common.cancel')}
                      </Button>
                      <Button type="submit">{t('paymentsTab.addPayment')}</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Reset Payments Button */}
              {paymentPlanConfig && (
                <AlertDialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" className="text-amber-600 border-amber-300 hover:bg-amber-50">
                      <RefreshCcw className="w-4 h-4 mr-2" />
                      {t('paymentsTab.resetPayments') || 'Reset Payments'}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle className="flex items-center gap-2">
                        <AlertCircle className="h-5 w-5 text-amber-500" />
                        {t('paymentsTab.resetPaymentsTitle') || 'Reset Payment Schedule?'}
                      </AlertDialogTitle>
                      <AlertDialogDescription className="text-left space-y-2">
                        <p>{t('paymentsTab.resetPaymentsWarning') || 'This will:'}</p>
                        <ul className="list-disc list-inside text-sm space-y-1">
                          <li>{t('paymentsTab.resetDeleteAll') || 'Delete ALL existing payments for this deal'}</li>
                          <li>{t('paymentsTab.resetDeleteTransactions') || 'Delete all recorded transactions (receipts, confirmations)'}</li>
                          <li>{t('paymentsTab.resetRegenerate') || 'Regenerate payments from the saved payment plan'}</li>
                        </ul>
                        <p className="font-medium text-destructive pt-2">
                          {t('paymentsTab.resetCannotUndo') || 'This action cannot be undone.'}
                        </p>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel disabled={isResetting}>
                        {t('common.cancel')}
                      </AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={handleResetPayments}
                        disabled={isResetting}
                        className="bg-amber-600 hover:bg-amber-700"
                      >
                        {isResetting ? (t('paymentsTab.resetting') || 'Resetting...') : (t('paymentsTab.resetPayments') || 'Reset Payments')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </>
          )}
        </div>
      </div>

      {/* Payment List */}
      <div className="space-y-3">
        {payments.filter(p => p && p.payment_type !== 'upsell_credit').map(payment => {
        const effectiveStatus = getEffectivePaymentStatus({
          status: payment.status,
          dueDate: payment.due_date,
          amount: payment.amount,
          paidAmount: payment.paid_amount,
          balance: payment.balance,
        });
        const StatusIcon = statusIcons[effectiveStatus] || AlertCircle;
        const daysOverdue = getDaysOverdue(payment.due_date);
        const isOverdue = daysOverdue > 0 && effectiveStatus !== 'paid' && effectiveStatus !== 'cancelled';
        
        // Get all transactions for this payment
        const allPaymentTransactions = transactionsByPayment[payment.id] || [];
        // Filter only successful transactions for displaying payment date
        const successfulTransactions = allPaymentTransactions.filter(
          (tx) => tx.status === 'confirmed' || tx.status === 'success' || tx.status === 'succeeded'
        );
        const transactionCount = successfulTransactions.length;
        const hasAnyTransactions = allPaymentTransactions.length > 0;
        
        
        return (
          <Card key={payment.id}>
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <h4 className="font-medium">{localizePaymentNumber(payment.payment_number)}</h4>
                    <Badge className={statusColors[effectiveStatus] || statusColors.pending}>
                      <StatusIcon className="w-3 h-3 mr-1" />
                      {statusLabelsPtBr[effectiveStatus] || effectiveStatus}
                    </Badge>
                    {isOverdue && (
                      <Badge variant="destructive" className="bg-red-100 text-red-800 border-red-300">
                        {t('paymentsTab.daysOverdue', { days: daysOverdue })}
                      </Badge>
                    )}
                    {/* Auto-Charge Badge + Toggle */}
                    {allowDealPaymentMutations && effectiveStatus !== 'paid' && effectiveStatus !== 'cancelled' && (
                      <div className="flex items-center gap-1.5 ml-auto">
                        {payment.auto_charge_enabled ? (
                          <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                            <Zap className="w-3 h-3 mr-1" />
                            Automático
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-gray-500 border-gray-200">
                            <ZapOff className="w-3 h-3 mr-1" />
                            Manual
                          </Badge>
                        )}
                        {hasCard && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center">
                                  <Switch
                                    checked={payment.auto_charge_enabled}
                                    onCheckedChange={(checked) => 
                                      toggleAutoChargeMutation.mutate({ 
                                        paymentId: payment.id, 
                                        enabled: checked 
                                      })
                                    }
                                    disabled={toggleAutoChargeMutation.isPending}
                                    className="scale-75"
                                  />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                {payment.auto_charge_enabled 
                                  ? 'Desativar cobrança automática' 
                                  : 'Ativar cobrança automática'}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    )}
                  </div>

                  {payment.description && <p className="text-sm text-muted-foreground mb-2">{localizePaymentDescription(payment.description)}</p>}

                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    <div>
                      <span className="text-muted-foreground">{t('paymentsTab.amount')}:</span>{' '}
                      <span className="font-medium">{formatCurrency(payment.amount)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">{t('paymentsTab.dueDate')}:</span>{' '}
                      <span className={isOverdue ? 'text-red-600 font-medium' : ''}>
                        {parseDate(payment.due_date) ? safeFormat(parseDate(payment.due_date)!, 'PP') : '-'}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">{t('paymentsTab.paid')}:</span>{' '}
                      <span className="font-medium text-green-600">{formatCurrency(payment.paid_amount)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">{t('paymentsTab.paymentDateLabel')}:</span>{' '}
                      {transactionCount === 1 ? (
                        parseDate(successfulTransactions[0].transaction_date) 
                          ? safeFormat(parseDate(successfulTransactions[0].transaction_date)!, 'PP') 
                          : '-'
                      ) : transactionCount > 1 ? (
                        <span className="text-blue-600">
                          {t('paymentsTab.multipleTransactions', { count: transactionCount, defaultValue: `${transactionCount} transações` })}
                        </span>
                      ) : '-'}
                    </div>
                    <div>
                      <span className="text-muted-foreground">{t('paymentsTab.balance')}:</span>{' '}
                      <span className="font-medium text-orange-600">{formatCurrency(payment.balance)}</span>
                    </div>
                  </div>

                  {/* Transaction History - Button to open modal */}
                  {hasAnyTransactions && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="w-full justify-between px-2 py-1 h-8 text-sm font-medium hover:bg-muted/50 mt-3"
                      onClick={() => {
                        setHistoryPaymentId(payment.id);
                        setHistoryPaymentDesc(payment.description || `Parcela ${payment.installment_number}`);
                      }}
                    >
                      <span className="flex items-center gap-2">
                        <History className="h-4 w-4" />
                        {t('paymentsTab.transactionHistory', 'Histórico de Transações')} ({transactionCount})
                      </span>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  )}

                  {payment.paid_amount > 0 && payment.paid_amount < payment.amount && (
                    <div className="mt-2">
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-green-500 transition-all" style={{
                          width: `${payment.paid_amount / payment.amount * 100}%`
                        }} />
                      </div>
                    </div>
                  )}

                  {/* Finance shortcuts (deal is mirror-only) */}
                  {effectiveStatus !== 'paid' && effectiveStatus !== 'cancelled' && (
                    <div className="mt-3 pt-3 border-t flex items-center gap-2">
                      <Button variant="outline" size="sm" asChild>
                        <Link to={`/finance/receivables?focus_payment=${payment.id}&action=charge&origin=deal&deal_id=${dealId}`}>
                          <DollarSign className="h-4 w-4 mr-1" />
                          Cobrar
                        </Link>
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setRegisterPaymentModalPayment({
                            id: payment.id,
                            amount: payment.amount,
                            paid_amount: payment.paid_amount,
                            balance: payment.balance ?? (payment.amount - (payment.paid_amount || 0)),
                            currency: (payment as any).currency,
                            description: payment.description,
                            business_unit: (payment as any).business_unit,
                          });
                        }}
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Dar Baixa
                      </Button>
                    </div>
                  )}

                  {/* Charge Actions - Only for unpaid payments (not upsell credits) */}
                  {allowDealPaymentMutations && contactId && payment.status !== 'paid' && payment.status !== 'cancelled' && payment.payment_type !== 'upsell_credit' && (payment.balance || 0) > 0 && (
                    <div className="mt-3 pt-3 border-t flex items-center gap-2">
                      <TooltipProvider>
                        {hasCard ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  console.log('🔵 Opening charge modal for payment:', payment.id);
                                  setChargeModalPayment({
                                    id: payment.id,
                                    amount: payment.amount,
                                    paid_amount: payment.paid_amount,
                                    installment_number: payment.installment_number,
                                    deal_name: dealName,
                                    client_name: contactName
                                  });
                                }}
                                disabled={chargeMutation.isPending}
                                className="text-green-600 border-green-200 hover:bg-green-50"
                              >
                                <CreditCard className="h-4 w-4 mr-1" />
                                Cobrar
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Cobrar do cartão salvo</TooltipContent>
                          </Tooltip>
                        ) : (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setLinkConfirmPaymentId(payment.id)}
                                disabled={linkMutation.isPending}
                              >
                                {linkMutation.isPending && linkConfirmPaymentId === payment.id ? (
                                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                ) : (
                                  <LinkIcon className="h-4 w-4 mr-1" />
                                )}
                                Gerar Link
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Gerar link de pagamento (sem cartão salvo)</TooltipContent>
                          </Tooltip>
                        )}
                        {hasCard && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setLinkConfirmPaymentId(payment.id)}
                                disabled={linkMutation.isPending}
                              >
                                <LinkIcon className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Gerar link alternativo</TooltipContent>
                          </Tooltip>
                        )}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setHistoryPaymentId(payment.id);
                                setHistoryPaymentDesc(`Parcela ${payment.installment_number || '-'} - ${formatCurrency(payment.amount)}`);
                              }}
                            >
                              <History className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Ver histórico de tentativas</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeletePaymentId(payment.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              disabled={payment.payment_type === 'upsell_credit'}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {payment.payment_type === 'upsell_credit' 
                              ? 'Crédito de upsell não pode ser removido' 
                              : 'Remover pagamento'}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}

        {payments.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              {t('paymentsTab.noPaymentsYet')}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Edit Transaction Dialog */}
      <Dialog open={isEditTransactionDialogOpen} onOpenChange={setIsEditTransactionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('paymentsTab.editTransaction', 'Editar Transação')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveEditedTransaction} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_transaction_date">{t('paymentsTab.paymentDate', 'Data')} *</Label>
                <Input 
                  id="edit_transaction_date" 
                  type="date" 
                  value={editTransactionFormData.transaction_date} 
                  onChange={e => setEditTransactionFormData(prev => ({
                    ...prev,
                    transaction_date: e.target.value
                  }))} 
                  required 
                />
              </div>
              <div>
                <Label htmlFor="edit_amount">{t('paymentsTab.amount', 'Valor')} *</Label>
                <Input 
                  id="edit_amount" 
                  type="number" 
                  step="0.01" 
                  value={editTransactionFormData.amount} 
                  onChange={e => setEditTransactionFormData(prev => ({
                    ...prev,
                    amount: e.target.value
                  }))} 
                  required 
                />
              </div>
            </div>

            <div>
              <Label htmlFor="edit_payment_method">{t('paymentsTab.paymentMethod', 'Método')} *</Label>
              <Select 
                value={editTransactionFormData.payment_method} 
                onValueChange={value => setEditTransactionFormData(prev => ({
                  ...prev,
                  payment_method: value
                }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="stripe">{t('paymentsTab.stripe')}</SelectItem>
                  <SelectItem value="zelle">{t('paymentsTab.zelle')}</SelectItem>
                  <SelectItem value="bank_transfer">{t('paymentsTab.bankTransfer')}</SelectItem>
                  <SelectItem value="cash">{t('paymentsTab.cash')}</SelectItem>
                  <SelectItem value="check">{t('paymentsTab.check')}</SelectItem>
                  <SelectItem value="pix">{t('paymentsTab.pix')}</SelectItem>
                  <SelectItem value="other">{t('paymentsTab.other')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="edit_reference">{t('paymentsTab.referenceNumber', 'Referência')}</Label>
              <Input 
                id="edit_reference" 
                value={editTransactionFormData.reference_number} 
                onChange={e => setEditTransactionFormData(prev => ({
                  ...prev,
                  reference_number: e.target.value
                }))} 
                placeholder={t('paymentsTab.referenceNumberPlaceholder', 'Número de referência')}
              />
            </div>

            <div>
              <Label htmlFor="edit_notes">{t('paymentsTab.notes', 'Observação')}</Label>
              <Textarea 
                id="edit_notes" 
                value={editTransactionFormData.notes} 
                onChange={e => setEditTransactionFormData(prev => ({
                  ...prev,
                  notes: e.target.value
                }))} 
                rows={2} 
                placeholder={t('paymentsTab.notesPlaceholder', 'Observações')}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setIsEditTransactionDialogOpen(false);
                  setEditingTransaction(null);
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button type="submit">
                {t('common.save', 'Salvar')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Charge Payment Modal with feedback */}
      <ChargePaymentModal
        open={!!chargeModalPayment}
        onOpenChange={(open) => {
          console.log('🔵 ChargePaymentModal onOpenChange:', open, 'current payment:', chargeModalPayment?.id);
          if (!open) {
            setChargeModalPayment(null);
            // Recarregar dados APÓS o modal fechar para mostrar resultado atualizado
            loadPayments();
            refetchTransactions();
          }
        }}
        payment={chargeModalPayment}
        onCharge={handleChargeWithModal}
        isCharging={chargeMutation.isPending}
      />

      {/* Inline Register Payment (Dar Baixa) Modal — keeps user inside the deal */}
      <RegisterPaymentInlineModal
        open={!!registerPaymentModalPayment}
        onOpenChange={(open) => {
          if (!open) setRegisterPaymentModalPayment(null);
        }}
        payment={registerPaymentModalPayment}
        clientName={contactName}
        dealId={dealId}
        onSuccess={() => {
          loadPayments();
          refetchTransactions();
        }}
      />

      {/* Link Generation Dialog */}
      <AlertDialog open={!!linkConfirmPaymentId} onOpenChange={() => setLinkConfirmPaymentId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Gerar Link de Pagamento</AlertDialogTitle>
            <AlertDialogDescription>
              {(() => {
                const payment = payments.find(p => p.id === linkConfirmPaymentId);
                if (!payment) return '';
                return `Será gerado um link de pagamento de ${formatCurrency(payment.balance || payment.amount)} para enviar ao cliente.`;
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={linkMutation.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const payment = payments.find(p => p.id === linkConfirmPaymentId);
                if (payment && linkConfirmPaymentId) {
                  linkMutation.mutate({ 
                    paymentId: linkConfirmPaymentId, 
                    amount: payment.balance || payment.amount 
                  });
                }
              }}
              disabled={linkMutation.isPending}
            >
              {linkMutation.isPending ? 'Gerando...' : 'Gerar Link'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Charge Attempts History Modal */}
      <ChargeAttemptsHistory
        open={!!historyPaymentId}
        onOpenChange={(open) => !open && setHistoryPaymentId(null)}
        paymentId={historyPaymentId}
        paymentDescription={historyPaymentDesc}
      />

      {/* Delete Payment Confirmation Dialog */}
      <AlertDialog open={!!deletePaymentId} onOpenChange={(open) => !open && setDeletePaymentId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-500" />
              Remover Pagamento?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {(() => {
                const payment = payments.find(p => p.id === deletePaymentId);
                if (!payment) return 'Esta ação não pode ser desfeita.';
                return `Tem certeza que deseja remover a parcela ${payment.installment_number || '-'} de ${formatCurrency(payment.amount)}? Esta ação não pode ser desfeita.`;
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingPayment}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!deletePaymentId) return;
                try {
                  setIsDeletingPayment(true);
                  await deletePayment(deletePaymentId);
                  setDeletePaymentId(null);
                } catch (error) {
                  console.error('Error deleting payment:', error);
                } finally {
                  setIsDeletingPayment(false);
                }
              }}
              disabled={isDeletingPayment}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeletingPayment ? 'Removendo...' : 'Remover'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Link Transaction Modal (finance-only operation, hidden in mirror mode) */}
      {allowDealPaymentMutations && (
        <LinkTransactionModal
          open={linkTransactionModalOpen}
          onOpenChange={setLinkTransactionModalOpen}
          transaction={selectedUnlinkedTransaction}
          payments={payments}
          onSuccess={() => {
            loadPayments();
            refetchTransactions();
          }}
        />
      )}
    </div>
  );
}
